import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { addEvent, getEvent, updateEvents } from '../api/events';
import { getTickets } from '../api/tickets';
import { getModuleFields } from '../api/modules';

import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';
import useRequired from '../hooks/useRequired';

import { classNames, getUserFullName } from '../components/helpers/functions';
import Modal from '../components/Shell/Modal';
import { AvatarGenerated } from '../components/Avatar';
import Button from '../components/Button';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import Tooltip from '../components/Tooltip';
import BetaAttributes, { configurePayload } from '../components/Attributes';

const MODAL_NAME = 'eventAdd';
const DEFAULT_FORM = {
	//assets: [],
	//locations: [],
	//vendors: [],
	//users: [],
	//tickets: [],
	//startTime: '9:0',
	//endTime: '10:0',
	//allDayEvent: true,
};

const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

export default () => {
	const { open: options, setClose } = useModal(MODAL_NAME);
	const [addAssetModalOpen, setAddAssetModalOpen] = useState(null);
	const [addVendorModalOpen, setAddVendorModalOpen] = useState(null);
	const [addTeamMemberModalOpen, setAddTeamMemberModalOpen] = useState(null);
	const [addTicketModalOpen, setAddTicketModalOpen] = useState(null);

	const [form, setForm] = useState(DEFAULT_FORM);
	const [updatedForm, setUpdateForm] = useState({});
	const [isEditing, setIsEditing] = useState(false);

	const { execute: onAddEvent, status: isAddingEvent } = useAsync(addEvent, {
		immediate: false,
	});
	const { execute: onUpdateEvent } = useAsync(updateEvents, {
		immediate: false,
	});
	const {
		value: tickets,
		execute: onGetTickets,
		status: isLoadingTickets,
	} = useAsync(getTickets, { immediate: false });

	const { value: event, execute: onGetEvent } = useAsync(getEvent, {
		immediate: false,
	});

	const { value: moduleFields, status: isLoadingModuleFields } = useAsync(
		getModuleFields,
		{
			defaultPayload: { id: EVENT_MODULE_ID, active: true },
		}
	);

	useEffect(() => {
		// Get event when modal opens
		if (options?.id) {
			onGetEvent({ id: options?.id });
			setIsEditing(true);
		} else {
			setIsEditing(false);
		}
	}, [options?.id]);

	useEffect(() => {
		if (event) {
			// 1. On update attempt, create update object
			let update = {};

			const CONVERTER = {
				'event-title': 'title',
				date: 'scheduledDate',
				'event-type': 'categoryId',
			};

			// Update update object with event fields
			moduleFields.map((field) => {
				const key = CONVERTER[field.key] || field?.key;
				if (event[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: event[key],
					};
				}
			});

			// Custom functions()
			if (event['scheduledDateEnd']) {
				update['start-time'] = {
					value: `${moment(event['scheduledDate']).get('h')}:${moment(
						event['scheduledDate']
					).get('m')}`,
				};
				update['end-time'] = {
					value: `${moment(event['scheduledDateEnd']).get('h')}:${moment(
						event['scheduledDateEnd']
					).get('m')}`,
				};
			} else {
				update['all-day-event'] = { value: true };
			}

			// Update form with event form
			setForm({ ...update });
		}
	}, [event]);

	useEffect(() => {
		// Open Modal
		if (!options) {
			setForm({});
			setUpdateForm({});
		}

		if (options) {
			let update = {};

			if (moduleFields?.length) {
				moduleFields
					.filter((field) => field?.type === 'boolean')
					.map((field) => {
						update[field.key] = {
							fieldId: field?.id,
							value: field?.defaultValue,
						};
					});
			}
			setForm({ ...form, ...update });
		}
	}, [options, moduleFields]);

	const PillTemplate = ({
		asset,
		vendor,
		user,
		ticket,
		i,
		isRemovable = true,
	}) => {
		let onClick = () => {};
		let avatarType = null;
		let id = null;
		let children = null;
		let border = '';
		let tooltip = false;

		if (asset) {
			onClick = () =>
				setForm({
					...form,
					assets: form?.assets.filter((a) => a.id !== asset.id),
				});
			avatarType = 'asset';
			id = asset.id;
			children = <div>{`${asset?.manufacturerName} ${asset?.modelName}`}</div>;
			border = 'border-cyan-600/60';
			tooltip = <div className="text-xxs">{`S/N: ${asset?.serialNumber}`}</div>;
		} else if (vendor) {
			onClick = () =>
				setForm({
					...form,
					vendors: form?.vendors.filter((a) => a.id !== vendor.id),
				});
			avatarType = vendor.type === 'company' ? 'company' : 'person';
			id = vendor.type === 'company' ? vendor.id : getUserFullName(vendor);
			children = (
				<>
					<div>{vendor?.companyName || getUserFullName(vendor)}</div>
				</>
			);
			border = 'border-yellow-600/60 ';
		} else if (user) {
			onClick = () =>
				setForm({
					...form,
					users: form?.users.filter((a) => a.id !== user.id),
				});
			avatarType = 'person';
			id = getUserFullName(user);
			children = (
				<>
					<div>{getUserFullName(user)}</div>
				</>
			);
			border = 'border-green-600/60 ';
		} else if (ticket) {
			onClick = () =>
				setForm({
					...form,
					tickets: form?.tickets.filter((a) => a.id !== ticket.id),
				});
			children = (
				<>
					<div>{ticket?.ticketCode}</div>
				</>
			);
			border = 'border-purple-600/60 ';
		}

		const Container = ({ children, message }) =>
			tooltip ? (
				<Tooltip message={message} i={i}>
					<div className="flex items-center">{children}</div>
				</Tooltip>
			) : (
				children
			);
		return (
			<div
				className={classNames(
					'text-xxs border rounded-2xl py-2 px-3 text-gray-500 mr-2 mb-2',
					border
				)}
			>
				<div className="flex items-center">
					<Container message={tooltip}>
						{avatarType && (
							<AvatarGenerated type={avatarType} id={id} size="xxs" />
						)}
						<div className="ml-2 mr-2">{children}</div>
					</Container>

					{isRemovable && (
						<XMarkIcon
							className="w-4 h-4 text-gray-500 ml-2  cursor-pointer"
							onClick={onClick}
						/>
					)}
				</div>
			</div>
		);
	};

	const groupedTickets = tickets?.rows?.reduce(
		(all, ticket) => {
			if (ticket.status === 'done' || ticket.status === 'closed') {
				all.done.push(ticket);
			} else {
				all[ticket.status].push(ticket);
			}

			return all;
		},
		{
			incoming: [],
			inprogress: [],
			done: [],
		}
	);

	// Has All Required
	let activeForm = {};
	const requiredForm = { ...form, ...updatedForm };
	Object.keys(requiredForm)
		.filter((key) => requiredForm[key]?.value)
		.map((key) => (activeForm[key] = requiredForm[key]));

	const hasRequired = useRequired(
		activeForm,
		moduleFields?.filter((field) => field.isRequired).map((field) => field.key)
	);
	return (
		<>
			<Modal
				title={isEditing ? 'Edit Event' : 'Add an Event'}
				name={MODAL_NAME}
				key={MODAL_NAME}
			>
				{isLoadingModuleFields === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
							{moduleFields
								?.filter((field) =>
									options?.assetId || isEditing
										? field?.key !== 'assetsList'
										: true
								)
								.map((field) => (
									<BetaAttributes
										field={field}
										onChange={(index, value, fullValue) => {
											if (isEditing) {
												let additional = {};
												if (index === 'all-day-event') {
													additional = {
														['start-time']: form['start-time'],
														['end-time']: form['end-time'],
													};
												}
												setUpdateForm({
													...additional,
													...updatedForm,
													[index]: {
														value: value || fullValue,
														fieldId: field.id,
													},
												});
											} else if (value || fullValue) {
												setForm({
													...(isEditing ? updatedForm : form),
													[index]: {
														value: value || fullValue,
														fieldId: field.id,
													},
												});
											} else {
												let toUpdateForm = {
													...form,
												};
												delete toUpdateForm[index];

												setForm(toUpdateForm);
											}
										}}
										form={{ ...form, ...updatedForm }}
										moduleId={EVENT_MODULE_ID}
									/>
								))}
						</div>
					</>
				)}

				<div className="grid grid-cols-3 gap-y-4 gap-x-4 text-left">
					{/*}

					<div className="col-span-2">
						<div className="flex items-center flex-wrap mt-2">
							{options?.asset ? (
								<PillTemplate
									asset={options?.asset}
									i={1}
									isRemovable={false}
								/>
							) : (
								form?.assets?.map((asset, i) => (
									<PillTemplate asset={asset} i={i} />
								))
							)}
							{form?.vendors?.map((vendor, i) => (
								<PillTemplate vendor={vendor} i={i} />
							))}
							{form?.users?.map((user, i) => (
								<PillTemplate user={user} i={i} />
							))}
							{form?.tickets?.map((ticket, i) => (
								<PillTemplate ticket={ticket} i={i} />
							))}
						</div>

						<div className="flex items-center flex-wrap mt-2">
							{!options?.asset && (
								<div
									className="text-xxs border rounded-2xl py-2 px-3 text-gray-500 cursor-pointer mr-2 mb-2"
									onClick={() => setAddAssetModalOpen(true)}
								>
									<div className="flex">
										<PlusIcon className="w-4 h-4 text-gray-500 mr-2" />
										<span>Add Asset</span>
									</div>
								</div>
							)}
							<div
								className="text-xxs border rounded-2xl py-2 px-3 text-gray-500 cursor-pointer mr-2 mb-2"
								onClick={() => setAddVendorModalOpen(true)}
							>
								<div className="flex">
									<PlusIcon className="w-4 h-4 text-gray-500 mr-2" />
									<span>Add Vendor</span>
								</div>
							</div>
							<div
								className="text-xxs border rounded-2xl py-2 px-3 text-gray-500 cursor-pointer mr-2 mb-2"
								onClick={() => setAddTeamMemberModalOpen(true)}
							>
								<div className="flex">
									<PlusIcon className="w-4 h-4 text-gray-500 mr-2" />
									<span>Add Team Member</span>
								</div>
							</div>
							<div
								className="text-xxs border rounded-2xl py-2 px-3 text-gray-500 cursor-pointer mr-2 mb-2"
								onClick={() => {
									onGetTickets();
									setAddTicketModalOpen(true);
								}}
							>
								<div className="flex">
									<PlusIcon className="w-4 h-4 text-gray-500 mr-2" />
									<span>Add Ticket</span>
								</div>
							</div>
						</div>
						
					</div>	*/}
				</div>

				<div className="flex justify-end mt-4">
					<Button
						disabled={!hasRequired}
						onClick={() => {
							if (isEditing) {
								onUpdateEvent(
									{
										id: event?.id,
										...configurePayload(updatedForm, moduleFields),
										/*title: form?.title,
										...(form?.allDayEvent
											? { date: moment(date).set('h', 0), endDate: null }
											: {
													date: moment(date).set('h', sh).set('m', sm),
													endDate: moment(date).set('h', eh).set('m', em),
											  }),

										...(form?.description
											? { description: form?.description }
											: {}),
										...(form?.assets?.length
											? { assets: form?.assets?.map((asset) => asset.id) }
											: {}),
										...(form?.vendors?.length
											? {
													vendors: form?.vendors?.map((vendor) => vendor.id),
											  }
											: {}),
										...(form?.users?.length
											? { users: form?.users?.map((user) => user.id) }
											: {}),
										...(form?.tickets?.length
											? {
													tickets: form?.tickets?.map((ticket) => ticket.id),
											  }
											: {}),*/
									},
									() => setClose()
								);
							} else {
								onAddEvent(
									{
										...configurePayload(form, moduleFields),
										...(options?.assetId ? { assets: [options?.assetId] } : {}),
									},
									() => setClose()
								);
							}
						}}
					>
						{isEditing
							? 'Update'
							: isAddingEvent === 'pending'
							? 'Adding event...'
							: 'Add event'}
					</Button>
				</div>
			</Modal>

			{/*<Modal
				title="Add Asset"
				open={addAssetModalOpen}
				onClose={() => setAddAssetModalOpen(null)}
			>
				<SelectAsset
					skip={form.assets.map((asset) => asset.id)}
					onChange={(asset) => {
						setAddAssetModalOpen(null);
						setForm({ ...form, assets: [...form.assets, asset] });
					}}
				/>
			</Modal>

			<Modal
				title="Add Vendor"
				open={addVendorModalOpen}
				onClose={() => setAddVendorModalOpen(null)}
			>
				<SelectContacts
					skip={form.vendors.map((vendor) => vendor.id)}
					onChange={(vendor) => {
						setAddVendorModalOpen(null);
						setForm({ ...form, vendors: [...form.vendors, vendor] });
					}}
				/>
			</Modal>

			<Modal
				title="Add Team Member"
				open={addTeamMemberModalOpen}
				onClose={() => setAddTeamMemberModalOpen(null)}
			>
				<SelectTeamMembers
					skip={form?.users?.map((user) => user.id)}
					onChange={(user) => {
						setAddTeamMemberModalOpen(null);
						setForm({ ...form, users: [...form?.users, user] });
					}}
				/>
			</Modal>

			<Modal
				title="Add Ticket"
				open={addTicketModalOpen}
				onClose={() => setAddTicketModalOpen(null)}
			>
				{isLoadingTickets === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<TemplateReactSelect
							name="Tickets"
							filterOption={(option, searchText) => {
								if (
									option.data.label
										.toLowerCase()
										.includes(searchText.toLowerCase()) ||
									option.data.ticket?.location?.name
										?.toLowerCase()
										.includes(searchText.toLowerCase()) ||
									option.data.ticket?.asset?.serialNumber
										?.toLowerCase()
										.includes(searchText.toLowerCase()) ||
									option.data.ticket?.asset?.manufacturerName
										?.toLowerCase()
										.includes(searchText.toLowerCase()) ||
									option.data.ticket?.asset?.modelName
										?.toLowerCase()
										.includes(searchText.toLowerCase())
								) {
									return true;
								} else {
									return false;
								}
							}}
							CustomOption={({ innerProps, isDisabled, ...rest }) => {
								const ticket = rest?.data?.ticket;
								const ticketLabel = rest?.data?.label;
								let ticketDesc = rest?.data?.ticket?.location?.name || '';

								if (rest?.data?.ticket?.asset) {
									ticketDesc = getStandardAssetName({
										...rest?.data?.ticket?.asset,
									});
								}
								return (
									<div
										{...innerProps}
										className={classNames(
											'px-3 py-2  text-xs',
											isDisabled ? '' : 'cursor-pointer hover:bg-indigo-50'
										)}
									>
										{ticket ? (
											<>
												<div>
													<div>{ticketLabel}</div>
													<div className="text-gray-500">{ticketDesc}</div>
												</div>
											</>
										) : (
											rest?.data?.label
										)}
									</div>
								);
							}}
							options={[
								{
									label: 'Incoming',
									options: groupedTickets?.incoming?.length
										? groupedTickets?.incoming?.map((ticket) => ({
												label: `${ticket?.ticketCode}`,
												value: ticket?.id,
												ticket,
										  }))
										: [{ label: `No tickets`, disabled: true }],
								},
								{
									label: 'In Progress',
									options: groupedTickets?.inprogress?.length
										? groupedTickets?.inprogress?.map((ticket) => ({
												label: `${ticket?.ticketCode}`,
												value: ticket?.id,
												ticket,
										  }))
										: [{ label: `No tickets`, disabled: true }],
								},
								{
									label: 'Done/Closed',
									options: groupedTickets?.done?.length
										? groupedTickets?.done?.map((ticket) => ({
												label: `${ticket?.ticketCode}`,
												value: ticket?.id,
												ticket,
										  }))
										: [{ label: `No tickets`, disabled: true }],
								},
							]}
							onChange={(index, ticket) => {
								setAddTicketModalOpen(null);
								setForm({
									...form,
									tickets: [...form?.tickets, ticket?.ticket],
								});
							}}
						/>
					</>
				)}
						</Modal>*/}
		</>
	);
};
